/**
 * The 2FA enrollment gate (standing policy: two-factor, always).
 * A signed-in user with no enrolled factor lands here before anything
 * else: scan the QR (or type the secret) into an authenticator app,
 * confirm one code, and the account is enrolled. Once every user is
 * enrolled, the server side flips ENABLED -> MANDATORY and this gate
 * becomes belt-and-braces.
 */
import { useEffect, useState } from 'react';
import { ACCENT, INK } from '../../../theme';
import QRCode from 'qrcode';
import type { TotpSecret } from 'firebase/auth';
import { finishTotpEnrollment, signOutUser, type User } from '../session';
import { startTotpEnrollment } from '../session';

interface TotpEnrollProps {
  user: User;
  onEnrolled: () => void;
}

export function TotpEnroll({ user, onEnrolled }: TotpEnrollProps) {
  const [secret, setSecret] = useState<TotpSecret | null>(null);
  const [secretKey, setSecretKey] = useState<string>('');
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    startTotpEnrollment(user)
      .then(async ({ secret, otpauthUri }) => {
        if (cancelled) return;
        setSecret(secret);
        setSecretKey(secret.secretKey);
        setQrDataUrl(await QRCode.toDataURL(otpauthUri, { width: 200, margin: 1 }));
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
    return () => {
      cancelled = true;
    };
  }, [user]);

  const confirm = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!secret) return;
    setBusy(true);
    setError(null);
    try {
      await finishTotpEnrollment(user, secret, code.trim());
      onEnrolled();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app-container" style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', backgroundColor: '#0f204b' }}>
      <div className="glass-panel" style={{ padding: '2rem', width: '340px', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.25rem' }}>Set up two-factor</h1>
        <div style={{ fontSize: '13px', color: INK.secondary }}>
          Polywog requires a second factor. Scan with your authenticator app
          (1Password, Google Authenticator, ...), then confirm a code.
        </div>
        {qrDataUrl ? (
          <img src={qrDataUrl} alt="TOTP enrollment QR" style={{ alignSelf: 'center', borderRadius: '8px' }} />
        ) : (
          <div style={{ textAlign: 'center', color: INK.muted, fontSize: '13px' }}>Generating…</div>
        )}
        {secretKey && (
          <div style={{ fontSize: '11px', color: INK.muted, wordBreak: 'break-all' }}>
            Manual key: <code>{secretKey}</code>
          </div>
        )}
        <form onSubmit={(e) => void confirm(e)} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <input
            inputMode="numeric"
            pattern="[0-9]*"
            required
            placeholder="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            style={{ padding: '0.5rem', borderRadius: '4px', border: `1px solid ${INK.faint}`, background: '#111827', color: INK.primary }}
          />
          <button type="submit" disabled={busy || !secret} style={{ padding: '0.5rem', borderRadius: '4px', border: `1px solid ${INK.faint}`, cursor: 'pointer' }}>
            {busy ? 'Confirming…' : 'Confirm'}
          </button>
        </form>
        {error && <div style={{ color: ACCENT.red, fontSize: '13px' }}>{error}</div>}
        <button
          onClick={() => void signOutUser()}
          style={{ background: 'none', border: 'none', color: INK.muted, cursor: 'pointer', fontSize: '12px' }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
